import { type ChangeEvent, type FC, useDeferredValue, useMemo } from "react";

import type { SourceControlRepository } from "../../types";

import { Button } from "../common/Button";
import {
  RETRIEVAL_MODE_OPTIONS,
  type RepositoryChatRetrievalMode,
} from "./repositoryChatModels";
import { RepositoryChatRepositoryPicker } from "./RepositoryChatRepositoryPicker";

interface RepositoryChatBuilderSectionProps {
  creatingConversation: boolean;
  loadingRepositories: boolean;
  onCreateConversation: () => void;
  onRepoSearchChange: (value: string) => void;
  onRetrievalModeChange: (value: RepositoryChatRetrievalMode) => void;
  onToggleRepository: (repository: SourceControlRepository) => void;
  repoSearch: string;
  repositories: SourceControlRepository[];
  retrievalMode: RepositoryChatRetrievalMode;
  selectedRepositorySlugs: string[];
}

export const RepositoryChatBuilderSection: FC<
  RepositoryChatBuilderSectionProps
> = ({
  creatingConversation,
  loadingRepositories,
  onCreateConversation,
  onRepoSearchChange,
  onRetrievalModeChange,
  onToggleRepository,
  repoSearch,
  repositories,
  retrievalMode,
  selectedRepositorySlugs,
}) => {
  const deferredSearch = useDeferredValue(repoSearch);
  const selectedProviderId = useMemo(
    () => resolveSelectedProviderId(repositories, selectedRepositorySlugs),
    [repositories, selectedRepositorySlugs],
  );
  const filteredRepositories = useMemo(
    () => filterRepositories(repositories, deferredSearch),
    [deferredSearch, repositories],
  );

  return (
    <section className="space-y-5">
      <ConversationBuilder
        creatingConversation={creatingConversation}
        filteredRepositories={filteredRepositories}
        loadingRepositories={loadingRepositories}
        onCreateConversation={onCreateConversation}
        onRepoSearchChange={onRepoSearchChange}
        onToggleRepository={onToggleRepository}
        repoSearch={repoSearch}
        selectedProviderId={selectedProviderId}
        selectedRepositorySlugs={selectedRepositorySlugs}
      />
      <RetrievalModeSection
        onRetrievalModeChange={onRetrievalModeChange}
        retrievalMode={retrievalMode}
      />
    </section>
  );
};

const ConversationBuilder: FC<{
  creatingConversation: boolean;
  filteredRepositories: SourceControlRepository[];
  loadingRepositories: boolean;
  onCreateConversation: () => void;
  onRepoSearchChange: (value: string) => void;
  onToggleRepository: (repository: SourceControlRepository) => void;
  repoSearch: string;
  selectedProviderId: null | string;
  selectedRepositorySlugs: string[];
}> = ({
  creatingConversation,
  filteredRepositories,
  loadingRepositories,
  onCreateConversation,
  onRepoSearchChange,
  onToggleRepository,
  repoSearch,
  selectedProviderId,
  selectedRepositorySlugs,
}) => (
  <section className="space-y-3">
    <HeaderLabel label="New conversation" />
    <label className="block space-y-1.5">
      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        Search repositories
      </span>
      <input
        className="w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-emerald-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        onChange={(event) => onRepoSearchChange(event.target.value)}
        placeholder="owner/repository"
        value={repoSearch}
      />
    </label>

    <RepositoryChatRepositoryPicker
      filteredRepositories={filteredRepositories}
      loadingRepositories={loadingRepositories}
      onToggleRepository={onToggleRepository}
      selectedProviderId={selectedProviderId}
      selectedRepositorySlugs={selectedRepositorySlugs}
    />

    <div className="space-y-2 border border-dashed border-slate-300 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900/40">
      <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        The repository set is fixed to your selection. Arti can narrow the work
        inside that scope, but it will not widen it automatically.
      </p>
      <Button
        disabled={selectedRepositorySlugs.length === 0}
        fullWidth
        loading={creatingConversation}
        loadingText="Starting chat…"
        onClick={onCreateConversation}
        type="button"
      >
        Start Repository Chat
      </Button>
    </div>
  </section>
);

const RetrievalModeSection: FC<{
  onRetrievalModeChange: (value: RepositoryChatRetrievalMode) => void;
  retrievalMode: RepositoryChatRetrievalMode;
}> = ({ onRetrievalModeChange, retrievalMode }) => (
  <section className="space-y-3">
    <HeaderLabel label="Response mode" />
    <label className="block space-y-1.5">
      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        Retrieval strategy
      </span>
      <select
        className="w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-emerald-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        onChange={(event: ChangeEvent<HTMLSelectElement>) =>
          onRetrievalModeChange(parseRetrievalMode(event.target.value))
        }
        value={retrievalMode}
      >
        {RETRIEVAL_MODE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
    <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
      {
        RETRIEVAL_MODE_OPTIONS.find((option) => option.value === retrievalMode)
          ?.description
      }
    </p>
  </section>
);

const HeaderLabel: FC<{ label: string }> = ({ label }) => (
  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
    {label}
  </p>
);

function filterRepositories(
  repositories: SourceControlRepository[],
  search: string,
): SourceControlRepository[] {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) {
    return repositories;
  }

  return repositories.filter((repository) =>
    repository.remote_slug.toLowerCase().includes(normalizedSearch),
  );
}

function parseRetrievalMode(value: string): RepositoryChatRetrievalMode {
  return (
    RETRIEVAL_MODE_OPTIONS.find((option) => option.value === value)?.value ??
    "hybrid"
  );
}

function resolveSelectedProviderId(
  repositories: SourceControlRepository[],
  selectedRepositorySlugs: string[],
): null | string {
  const [firstSelectedSlug] = selectedRepositorySlugs;
  if (!firstSelectedSlug) {
    return null;
  }

  return (
    repositories.find(
      (repository) => repository.remote_slug === firstSelectedSlug,
    )?.provider_id ?? null
  );
}
